"use client"

import { useEffect, useState } from "react"

// Define TypeScript interfaces
interface ChartNode {
  id: string
  code: string
  title: string
  children: ChartNode[]
}

const AccountChart = () => {
  const [accChartTree, setAccChartTree] = useState<ChartNode[]>([])
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())

  // Expanded dummy data for account chart tree - School specific with generators, buses, etc.
  const dummyAccChartTree: ChartNode[] = [
    {
      id: "100000",
      code: "100000",
      title: "CHART OF ACCOUNT",
      children: [
        {
          id: "10000",
          code: "10000",
          title: "REVENUE",
          children: [
            {
              id: "11000",
              code: "11000",
              title: "Tuition Fees",
              children: [
                {
                  id: "11100",
                  code: "11100",
                  title: "Primary School Fees",
                  children: [],
                },
                {
                  id: "11200",
                  code: "11200",
                  title: "Secondary School Fees",
                  children: [],
                },
                {
                  id: "11300",
                  code: "11300",
                  title: "Kindergarten Fees",
                  children: [],
                },
                {
                  id: "11400",
                  code: "11400",
                  title: "Special Education Fees",
                  children: [],
                },
              ],
            },
            {
              id: "12000",
              code: "12000",
              title: "Grants & Donations",
              children: [
                {
                  id: "12100",
                  code: "12100",
                  title: "Government Grants",
                  children: [],
                },
                {
                  id: "12200",
                  code: "12200",
                  title: "Private Donations",
                  children: [],
                },
                {
                  id: "12300",
                  code: "12300",
                  title: "Alumni Contributions",
                  children: [],
                },
                {
                  id: "12400",
                  code: "12400",
                  title: "Corporate Sponsorships",
                  children: [],
                },
              ],
            },
            {
              id: "13000",
              code: "13000",
              title: "Other Income",
              children: [
                {
                  id: "13100",
                  code: "13100",
                  title: "Cafeteria Sales",
                  children: [],
                },
                {
                  id: "13200",
                  code: "13200",
                  title: "Uniform Sales",
                  children: [],
                },
                {
                  id: "13300",
                  code: "13300",
                  title: "Book Sales",
                  children: [],
                },
                {
                  id: "13400",
                  code: "13400",
                  title: "School Events",
                  children: [],
                },
              ],
            },
            {
              id: "14000",
              code: "14000",
              title: "Transportation Services",
              children: [
                {
                  id: "14100",
                  code: "14100",
                  title: "School Bus Fees",
                  children: [],
                },
                {
                  id: "14200",
                  code: "14200",
                  title: "Field Trip Transportation",
                  children: [],
                },
                {
                  id: "14300",
                  code: "14300",
                  title: "Special Event Transportation",
                  children: [],
                },
              ],
            },
            {
              id: "15000",
              code: "15000",
              title: "Facility Rentals",
              children: [
                {
                  id: "15100",
                  code: "15100",
                  title: "Auditorium Rental",
                  children: [],
                },
                {
                  id: "15200",
                  code: "15200",
                  title: "Sports Field Rental",
                  children: [],
                },
                {
                  id: "15300",
                  code: "15300",
                  title: "Classroom Rental",
                  children: [],
                },
              ],
            },
          ],
        },
        {
          id: "20000",
          code: "20000",
          title: "EXPENSES",
          children: [
            {
              id: "21000",
              code: "21000",
              title: "Staff Salaries & Benefits",
              children: [
                {
                  id: "21100",
                  code: "21100",
                  title: "Teaching Staff Salaries",
                  children: [],
                },
                {
                  id: "21200",
                  code: "21200",
                  title: "Administrative Staff Salaries",
                  children: [],
                },
                {
                  id: "21300",
                  code: "21300",
                  title: "Support Staff Salaries",
                  children: [],
                },
                {
                  id: "21400",
                  code: "21400",
                  title: "Employee Benefits",
                  children: [],
                },
                {
                  id: "21500",
                  code: "21500",
                  title: "Staff Development",
                  children: [],
                },
              ],
            },
            {
              id: "22000",
              code: "22000",
              title: "Facilities & Maintenance",
              children: [
                {
                  id: "22100",
                  code: "22100",
                  title: "Building Maintenance",
                  children: [],
                },
                {
                  id: "22200",
                  code: "22200",
                  title: "Utilities",
                  children: [
                    {
                      id: "22210",
                      code: "22210",
                      title: "Electricity",
                      children: [],
                    },
                    {
                      id: "22220",
                      code: "22220",
                      title: "Water",
                      children: [],
                    },
                    {
                      id: "22230",
                      code: "22230",
                      title: "Internet & Telecommunications",
                      children: [],
                    },
                  ],
                },
                {
                  id: "22300",
                  code: "22300",
                  title: "Cleaning Services",
                  children: [],
                },
                {
                  id: "22400",
                  code: "22400",
                  title: "Security Services",
                  children: [],
                },
                {
                  id: "22500",
                  code: "22500",
                  title: "Generator Maintenance",
                  children: [
                    {
                      id: "22510",
                      code: "22510",
                      title: "Generator Fuel",
                      children: [],
                    },
                    {
                      id: "22520",
                      code: "22520",
                      title: "Generator Repairs",
                      children: [],
                    },
                    {
                      id: "22530",
                      code: "22530",
                      title: "Generator Parts",
                      children: [],
                    },
                  ],
                },
                {
                  id: "22600",
                  code: "22600",
                  title: "Grounds Maintenance",
                  children: [],
                },
              ],
            },
            {
              id: "23000",
              code: "23000",
              title: "Educational Materials",
              children: [
                {
                  id: "23100",
                  code: "23100",
                  title: "Textbooks",
                  children: [],
                },
                {
                  id: "23200",
                  code: "23200",
                  title: "Classroom Supplies",
                  children: [],
                },
                {
                  id: "23300",
                  code: "23300",
                  title: "Laboratory Equipment",
                  children: [],
                },
                {
                  id: "23400",
                  code: "23400",
                  title: "Library Resources",
                  children: [],
                },
                {
                  id: "23500",
                  code: "23500",
                  title: "Digital Learning Resources",
                  children: [],
                },
              ],
            },
            {
              id: "24000",
              code: "24000",
              title: "Technology",
              children: [
                {
                  id: "24100",
                  code: "24100",
                  title: "Computer Hardware",
                  children: [],
                },
                {
                  id: "24200",
                  code: "24200",
                  title: "Software Licenses",
                  children: [],
                },
                {
                  id: "24300",
                  code: "24300",
                  title: "Internet Services",
                  children: [],
                },
                {
                  id: "24400",
                  code: "24400",
                  title: "IT Support",
                  children: [],
                },
                {
                  id: "24500",
                  code: "24500",
                  title: "Educational Technology",
                  children: [],
                },
              ],
            },
            {
              id: "25000",
              code: "25000",
              title: "Transportation Expenses",
              children: [
                {
                  id: "25100",
                  code: "25100",
                  title: "School Bus Operations",
                  children: [
                    {
                      id: "25110",
                      code: "25110",
                      title: "Bus Fuel",
                      children: [],
                    },
                    {
                      id: "25120",
                      code: "25120",
                      title: "Bus Maintenance",
                      children: [],
                    },
                    {
                      id: "25130",
                      code: "25130",
                      title: "Bus Driver Salaries",
                      children: [],
                    },
                    {
                      id: "25140",
                      code: "25140",
                      title: "Bus Insurance",
                      children: [],
                    },
                  ],
                },
                {
                  id: "25200",
                  code: "25200",
                  title: "Field Trip Transportation",
                  children: [],
                },
                {
                  id: "25300",
                  code: "25300",
                  title: "Vehicle Rentals",
                  children: [],
                },
              ],
            },
            {
              id: "26000",
              code: "26000",
              title: "Administrative Expenses",
              children: [
                {
                  id: "26100",
                  code: "26100",
                  title: "Office Supplies",
                  children: [],
                },
                {
                  id: "26200",
                  code: "26200",
                  title: "Printing & Copying",
                  children: [],
                },
                {
                  id: "26300",
                  code: "26300",
                  title: "Postage & Shipping",
                  children: [],
                },
                {
                  id: "26400",
                  code: "26400",
                  title: "Legal & Professional Fees",
                  children: [],
                },
                {
                  id: "26500",
                  code: "26500",
                  title: "Insurance",
                  children: [],
                },
              ],
            },
          ],
        },
        {
          id: "30000",
          code: "30000",
          title: "ASSETS",
          children: [
            {
              id: "31000",
              code: "31000",
              title: "Current Assets",
              children: [
                {
                  id: "31100",
                  code: "31100",
                  title: "Cash & Bank Accounts",
                  children: [
                    {
                      id: "31110",
                      code: "31110",
                      title: "Operating Account",
                      children: [],
                    },
                    {
                      id: "31120",
                      code: "31120",
                      title: "Savings Account",
                      children: [],
                    },
                    {
                      id: "31130",
                      code: "31130",
                      title: "Petty Cash",
                      children: [],
                    },
                  ],
                },
                {
                  id: "31200",
                  code: "31200",
                  title: "Accounts Receivable",
                  children: [
                    {
                      id: "31210",
                      code: "31210",
                      title: "Student Fees Receivable",
                      children: [],
                    },
                    {
                      id: "31220",
                      code: "31220",
                      title: "Grants Receivable",
                      children: [],
                    },
                    {
                      id: "31230",
                      code: "31230",
                      title: "Other Receivables",
                      children: [],
                    },
                  ],
                },
                {
                  id: "31300",
                  code: "31300",
                  title: "Inventory",
                  children: [
                    {
                      id: "31310",
                      code: "31310",
                      title: "Textbook Inventory",
                      children: [],
                    },
                    {
                      id: "31320",
                      code: "31320",
                      title: "Uniform Inventory",
                      children: [],
                    },
                    {
                      id: "31330",
                      code: "31330",
                      title: "Cafeteria Inventory",
                      children: [],
                    },
                    {
                      id: "31340",
                      code: "31340",
                      title: "Office Supplies Inventory",
                      children: [],
                    },
                  ],
                },
                {
                  id: "31400",
                  code: "31400",
                  title: "Prepaid Expenses",
                  children: [
                    {
                      id: "31410",
                      code: "31410",
                      title: "Prepaid Insurance",
                      children: [],
                    },
                    {
                      id: "31420",
                      code: "31420",
                      title: "Prepaid Rent",
                      children: [],
                    },
                    {
                      id: "31430",
                      code: "31430",
                      title: "Prepaid Subscriptions",
                      children: [],
                    },
                  ],
                },
              ],
            },
            {
              id: "32000",
              code: "32000",
              title: "Fixed Assets",
              children: [
                {
                  id: "32100",
                  code: "32100",
                  title: "Land & Buildings",
                  children: [
                    {
                      id: "32110",
                      code: "32110",
                      title: "School Land",
                      children: [],
                    },
                    {
                      id: "32120",
                      code: "32120",
                      title: "School Buildings",
                      children: [],
                    },
                    {
                      id: "32130",
                      code: "32130",
                      title: "Building Improvements",
                      children: [],
                    },
                    {
                      id: "32140",
                      code: "32140",
                      title: "Sports Facilities",
                      children: [],
                    },
                  ],
                },
                {
                  id: "32200",
                  code: "32200",
                  title: "Furniture & Fixtures",
                  children: [
                    {
                      id: "32210",
                      code: "32210",
                      title: "Classroom Furniture",
                      children: [],
                    },
                    {
                      id: "32220",
                      code: "32220",
                      title: "Office Furniture",
                      children: [],
                    },
                    {
                      id: "32230",
                      code: "32230",
                      title: "Cafeteria Furniture",
                      children: [],
                    },
                  ],
                },
                {
                  id: "32300",
                  code: "32300",
                  title: "Vehicles",
                  children: [
                    {
                      id: "32310",
                      code: "32310",
                      title: "School Buses",
                      children: [],
                    },
                    {
                      id: "32320",
                      code: "32320",
                      title: "Administrative Vehicles",
                      children: [],
                    },
                    {
                      id: "32330",
                      code: "32330",
                      title: "Maintenance Vehicles",
                      children: [],
                    },
                  ],
                },
                {
                  id: "32400",
                  code: "32400",
                  title: "Equipment",
                  children: [
                    {
                      id: "32410",
                      code: "32410",
                      title: "Computer Equipment",
                      children: [],
                    },
                    {
                      id: "32420",
                      code: "32420",
                      title: "Laboratory Equipment",
                      children: [],
                    },
                    {
                      id: "32430",
                      code: "32430",
                      title: "Sports Equipment",
                      children: [],
                    },
                    {
                      id: "32440",
                      code: "32440",
                      title: "Generators & Power Equipment",
                      children: [
                        {
                          id: "32441",
                          code: "32441",
                          title: "Main Generator",
                          children: [],
                        },
                        {
                          id: "32442",
                          code: "32442",
                          title: "Backup Generators",
                          children: [],
                        },
                        {
                          id: "32443",
                          code: "32443",
                          title: "Solar Power Systems",
                          children: [],
                        },
                        {
                          id: "32444",
                          code: "32444",
                          title: "UPS Systems",
                          children: [],
                        },
                      ],
                    },
                    {
                      id: "32450",
                      code: "32450",
                      title: "Audio-Visual Equipment",
                      children: [],
                    },
                    {
                      id: "32460",
                      code: "32460",
                      title: "Kitchen Equipment",
                      children: [],
                    },
                  ],
                },
              ],
            },
            {
              id: "33000",
              code: "33000",
              title: "Intangible Assets",
              children: [
                {
                  id: "33100",
                  code: "33100",
                  title: "Software",
                  children: [],
                },
                {
                  id: "33200",
                  code: "33200",
                  title: "Intellectual Property",
                  children: [],
                },
                {
                  id: "33300",
                  code: "33300",
                  title: "Curriculum Development",
                  children: [],
                },
              ],
            },
          ],
        },
        {
          id: "40000",
          code: "40000",
          title: "LIABILITIES",
          children: [
            {
              id: "41000",
              code: "41000",
              title: "Current Liabilities",
              children: [
                {
                  id: "41100",
                  code: "41100",
                  title: "Accounts Payable",
                  children: [
                    {
                      id: "41110",
                      code: "41110",
                      title: "Vendor Payables",
                      children: [],
                    },
                    {
                      id: "41120",
                      code: "41120",
                      title: "Utility Payables",
                      children: [],
                    },
                    {
                      id: "41130",
                      code: "41130",
                      title: "Other Payables",
                      children: [],
                    },
                  ],
                },
                {
                  id: "41200",
                  code: "41200",
                  title: "Salaries Payable",
                  children: [
                    {
                      id: "41210",
                      code: "41210",
                      title: "Teaching Staff Payable",
                      children: [],
                    },
                    {
                      id: "41220",
                      code: "41220",
                      title: "Administrative Staff Payable",
                      children: [],
                    },
                    {
                      id: "41230",
                      code: "41230",
                      title: "Support Staff Payable",
                      children: [],
                    },
                  ],
                },
                {
                  id: "41300",
                  code: "41300",
                  title: "Short-term Loans",
                  children: [
                    {
                      id: "41310",
                      code: "41310",
                      title: "Bank Overdrafts",
                      children: [],
                    },
                    {
                      id: "41320",
                      code: "41320",
                      title: "Working Capital Loans",
                      children: [],
                    },
                  ],
                },
                {
                  id: "41400",
                  code: "41400",
                  title: "Deferred Revenue",
                  children: [
                    {
                      id: "41410",
                      code: "41410",
                      title: "Tuition Fees Received in Advance",
                      children: [],
                    },
                    {
                      id: "41420",
                      code: "41420",
                      title: "Grants Received in Advance",
                      children: [],
                    },
                  ],
                },
                {
                  id: "41500",
                  code: "41500",
                  title: "Taxes Payable",
                  children: [
                    {
                      id: "41510",
                      code: "41510",
                      title: "Payroll Taxes",
                      children: [],
                    },
                    {
                      id: "41520",
                      code: "41520",
                      title: "Property Taxes",
                      children: [],
                    },
                    {
                      id: "41530",
                      code: "41530",
                      title: "Other Taxes",
                      children: [],
                    },
                  ],
                },
              ],
            },
            {
              id: "42000",
              code: "42000",
              title: "Long-term Liabilities",
              children: [
                {
                  id: "42100",
                  code: "42100",
                  title: "Mortgages",
                  children: [
                    {
                      id: "42110",
                      code: "42110",
                      title: "Building Mortgage",
                      children: [],
                    },
                    {
                      id: "42120",
                      code: "42120",
                      title: "Land Mortgage",
                      children: [],
                    },
                  ],
                },
                {
                  id: "42200",
                  code: "42200",
                  title: "Long-term Loans",
                  children: [
                    {
                      id: "42210",
                      code: "42210",
                      title: "Equipment Loans",
                      children: [],
                    },
                    {
                      id: "42220",
                      code: "42220",
                      title: "Vehicle Loans",
                      children: [
                        {
                          id: "42221",
                          code: "42221",
                          title: "School Bus Loans",
                          children: [],
                        },
                        {
                          id: "42222",
                          code: "42222",
                          title: "Other Vehicle Loans",
                          children: [],
                        },
                      ],
                    },
                    {
                      id: "42230",
                      code: "42230",
                      title: "Development Loans",
                      children: [],
                    },
                  ],
                },
                {
                  id: "42300",
                  code: "42300",
                  title: "Bonds Payable",
                  children: [],
                },
                {
                  id: "42400",
                  code: "42400",
                  title: "Long-term Lease Obligations",
                  children: [
                    {
                      id: "42410",
                      code: "42410",
                      title: "Equipment Leases",
                      children: [],
                    },
                    {
                      id: "42420",
                      code: "42420",
                      title: "Vehicle Leases",
                      children: [],
                    },
                    {
                      id: "42430",
                      code: "42430",
                      title: "Property Leases",
                      children: [],
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          id: "50000",
          code: "50000",
          title: "EQUITY",
          children: [
            {
              id: "51000",
              code: "51000",
              title: "School Funds",
              children: [
                {
                  id: "51100",
                  code: "51100",
                  title: "General Fund",
                  children: [],
                },
                {
                  id: "51200",
                  code: "51200",
                  title: "Building Fund",
                  children: [],
                },
                {
                  id: "51300",
                  code: "51300",
                  title: "Technology Fund",
                  children: [],
                },
                {
                  id: "51400",
                  code: "51400",
                  title: "Scholarship Fund",
                  children: [],
                },
              ],
            },
            {
              id: "52000",
              code: "52000",
              title: "Reserves",
              children: [
                {
                  id: "52100",
                  code: "52100",
                  title: "Operating Reserves",
                  children: [],
                },
                {
                  id: "52200",
                  code: "52200",
                  title: "Capital Reserves",
                  children: [],
                },
                {
                  id: "52300",
                  code: "52300",
                  title: "Emergency Reserves",
                  children: [],
                },
              ],
            },
            {
              id: "53000",
              code: "53000",
              title: "Endowments",
              children: [
                {
                  id: "53100",
                  code: "53100",
                  title: "Permanent Endowments",
                  children: [],
                },
                {
                  id: "53200",
                  code: "53200",
                  title: "Term Endowments",
                  children: [],
                },
                {
                  id: "53300",
                  code: "53300",
                  title: "Quasi-Endowments",
                  children: [],
                },
              ],
            },
            {
              id: "54000",
              code: "54000",
              title: "Retained Earnings",
              children: [
                {
                  id: "54100",
                  code: "54100",
                  title: "Current Year Earnings",
                  children: [],
                },
                {
                  id: "54200",
                  code: "54200",
                  title: "Prior Year Earnings",
                  children: [],
                },
              ],
            },
          ],
        },
      ],
    },
  ]

  useEffect(() => {
    // Load initial data
    if (dummyAccChartTree && dummyAccChartTree.length > 0) {
      setAccChartTree(dummyAccChartTree)

      // Initialize all nodes as expanded
      const initialExpanded = new Set<string>()
      const addNodeIds = (nodes: ChartNode[]) => {
        nodes.forEach((node) => {
          initialExpanded.add(node.id)
          if (node.children && node.children.length > 0) {
            addNodeIds(node.children)
          }
        })
      }
      addNodeIds(dummyAccChartTree)
      setExpandedNodes(initialExpanded)
    }
  }, [])

  const handleExpandAll = () => {
    const allNodes = new Set<string>()
    const addNodeIds = (nodes: ChartNode[]) => {
      nodes.forEach((node) => {
        allNodes.add(node.id)
        if (node.children && node.children.length > 0) {
          addNodeIds(node.children)
        }
      })
    }
    addNodeIds(accChartTree)
    setExpandedNodes(allNodes)
    alert("All accounts expanded")
  }

  const handleCollapseAll = () => {
    setExpandedNodes(new Set())
    alert("All accounts collapsed")
  }

  const handleExportCSV = () => {
    try {
      // Create a CSV string
      let csvContent = "Account Code,Account Title,Parent Account\n"

      // Flatten the tree data
      const flatData = flattenTree(accChartTree)

      // Convert to CSV
      flatData.forEach((item) => {
        csvContent += `${item["Account Code"]},"${item["Account Title"].replace(/"/g, '""')}","${item["Parent Account"].replace(/"/g, '""')}"\n`
      })

      // Create Blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = "school_accounts.csv"
      link.click()

      // Clean up
      URL.revokeObjectURL(url)

      alert("Account chart exported successfully")
    } catch (error) {
      console.error("Error exporting to CSV:", error)
      alert("Failed to export account chart")
    }
  }

  const flattenTree = (nodes: ChartNode[], parentTitle = ""): any[] => {
    let result: any[] = []

    nodes.forEach((node) => {
      // Add current node
      result.push({
        "Account Code": node.code,
        "Account Title": node.title,
        "Parent Account": parentTitle,
      })

      // Add children recursively
      if (node.children && node.children.length > 0) {
        result = [...result, ...flattenTree(node.children, node.title)]
      }
    })

    return result
  }

  const toggleNode = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId)
      } else {
        newSet.add(nodeId)
      }
      return newSet
    })
  }

  const handleAddClick = (node: ChartNode) => {
    alert(`Add clicked for ${node.title}`)
  }

  const handleEditClick = (node: ChartNode) => {
    alert(`Edit clicked for ${node.title}`)
  }

  const handleDeleteClick = (node: ChartNode) => {
    alert(`Delete clicked for ${node.title}`)
  }

  // Filter function for search
  const filterTree = (nodes: ChartNode[], term: string): ChartNode[] => {
    if (!nodes || !term) return nodes || []

    return nodes.reduce((filtered: ChartNode[], node) => {
      const nodeMatches =
        node.title.toLowerCase().includes(term.toLowerCase()) || node.code.toLowerCase().includes(term.toLowerCase())

      const filteredChildren = filterTree(node.children || [], term)

      if (nodeMatches || filteredChildren.length > 0) {
        filtered.push({
          ...node,
          children: filteredChildren,
        })
      }

      return filtered
    }, [])
  }

  // Recursive component to render tree nodes
  const renderNode = (node: ChartNode, level = 0) => {
    const isExpanded = expandedNodes.has(node.id)
    const hasChildren = node.children && node.children.length > 0

    return (
      <div key={node.id} className="position-relative">
        {/* Vertical line from parent */}
        {level > 0 && (
          <div
            className="position-absolute border-start border-secondary"
            style={{
              left: `${(level - 1) * 40 + 12}px`,
              top: 0,
              height: "50%",
              width: "1px",
            }}
          ></div>
        )}

        {/* Horizontal line to node */}
        {level > 0 && (
          <div
            className="position-absolute border-top border-secondary"
            style={{
              left: `${(level - 1) * 40 + 12}px`,
              top: "24px",
              width: "28px",
              height: "1px",
            }}
          ></div>
        )}

        {/* Vertical line to children */}
        {hasChildren && isExpanded && (
          <div
            className="position-absolute border-start border-secondary"
            style={{
              left: `${level * 40 + 12}px`,
              top: "48px",
              bottom: 0,
              width: "1px",
            }}
          ></div>
        )}

        <div className="d-flex align-items-center py-2 position-relative" style={{ marginLeft: `${level * 40}px` }}>
          {/* Toggle button */}
          <button
            className="btn btn-sm btn-outline-secondary rounded-circle me-2 d-flex align-items-center justify-content-center"
            style={{ width: "24px", height: "24px", padding: "0" }}
            onClick={() => toggleNode(node.id)}
          >
            {hasChildren ? (
              isExpanded ? (
                <span className="small fw-bold">−</span>
              ) : (
                <span className="small fw-bold">+</span>
              )
            ) : (
              <span className="small fw-bold">+</span>
            )}
          </button>

          {/* Node content */}
          <div className="d-flex flex-grow-1 align-items-center">
            <div className="d-flex flex-grow-1 bg-white border rounded shadow-sm p-2 justify-content-between align-items-center">
              <span className="fw-medium">
                {node.code}-{node.title}
              </span>

              <div className="d-flex">
                <button
                  className="btn btn-sm btn-primary me-1 d-flex align-items-center justify-content-center"
                  style={{ width: "32px", height: "32px", padding: "0" }}
                  onClick={() => handleAddClick(node)}
                  title="Add"
                >
                  <i className="bi bi-plus-circle"></i>
                </button>

                <button
                  className="btn btn-sm btn-primary me-1 d-flex align-items-center justify-content-center"
                  style={{ width: "32px", height: "32px", padding: "0" }}
                  onClick={() => handleEditClick(node)}
                  title="Edit"
                >
                  <i className="bi bi-pencil"></i>
                </button>

                {!hasChildren && (
                  <button
                    className="btn btn-sm btn-danger d-flex align-items-center justify-content-center"
                    style={{ width: "32px", height: "32px", padding: "0" }}
                    onClick={() => handleDeleteClick(node)}
                    title="Delete"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Children */}
        {isExpanded && hasChildren && (
          <div className="ms-4">
            {node.children && node.children.map((childNode) => renderNode(childNode, level + 1))}
          </div>
        )}
      </div>
    )
  }

  const filteredData = searchTerm ? filterTree(accChartTree, searchTerm) : accChartTree

  return (
    <div className="card shadow">
      <div className="card-header bg-primary text-white">
        <h5 className="card-title mb-0">School Chart of Account Setup</h5>
      </div>
      <div className="card-body">
        <div className="row">
          <div className="col-md-4">
            <div className="mb-4">
              <div className="btn-group mb-3">
                <button className="btn btn-primary d-flex align-items-center" onClick={handleExpandAll}>
                  <i className="bi bi-chevron-down me-1"></i>
                  <span>Expand All</span>
                </button>
                <button className="btn btn-primary d-flex align-items-center" onClick={handleCollapseAll}>
                  <i className="bi bi-chevron-up me-1"></i>
                  <span>Collapse All</span>
                </button>
                <button className="btn btn-success d-flex align-items-center" onClick={handleExportCSV}>
                  <i className="bi bi-download me-1"></i>
                  <span>Export CSV</span>
                </button>
              </div>

              <div className="form-group">
                <label className="form-label">Search School Accounts</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search accounts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="col-md-8">
            <div
              className="account-tree-container border rounded p-3"
              style={{ maxHeight: "600px", overflowY: "auto" }}
            >
              {filteredData && filteredData.length > 0 ? (
                filteredData.map((node) => renderNode(node))
              ) : (
                <div className="text-center py-4 text-muted">No results found</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AccountChart
